import { useMemo } from 'react';
import {
  formatCentsAsEuros,
  sumQuoteDueAtSubscription,
} from '../../utils/nr2jConstants';
import { buildNr2jQuoteLivePreviewGroups } from '../../utils/nr2jQuoteLivePreview.js';

export { buildNr2jQuoteLivePreviewGroups, discountScopeAppliedLabel } from '../../utils/nr2jQuoteLivePreview.js';

/**
 * Panneau droit « Aperçu devis » — même rendu nouveau devis / devis existant.
 */
export default function Nr2jQuoteLivePreviewPanel({ lines, lineToPayload, title = 'Aperçu devis' }) {
  const livePreviewGroups = useMemo(
    () => buildNr2jQuoteLivePreviewGroups(lines, lineToPayload),
    [lines, lineToPayload],
  );
  const dueAtSubscription = useMemo(
    () => sumQuoteDueAtSubscription((lines || []).map((l, i) => lineToPayload(l, i))),
    [lines, lineToPayload],
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h2>
      <p className="mt-1 text-xs text-gray-500">
        Récapitulatif commercial en temps réel (miroir du détail devis / PDF).
      </p>
      <div className="mt-4 space-y-5 text-sm" data-testid="nr2j-quote-live-preview">
        {livePreviewGroups.length === 0 ? (
          <p className="text-sm text-gray-500">Ajoutez des lignes pour voir l’aperçu.</p>
        ) : null}
        {livePreviewGroups.map((group) => {
          const block = group.totals;
          const hasDiscount = (block?.totalDiscountHtCents || 0) > 0;
          return (
            <div
              key={group.billingType}
              className="space-y-3"
              data-testid={`nr2j-quote-preview-group-${group.billingType}`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
                {group.label}
              </p>
              <div className="space-y-3">
                {group.items.map((item) => (
                  <div
                    key={item.key}
                    className="rounded-lg border border-gray-100 bg-gray-50/80 p-3"
                    data-testid="nr2j-quote-preview-line"
                  >
                    <p className="font-medium text-gray-900">{item.name}</p>
                    {item.description ? (
                      <p className="mt-0.5 text-xs text-gray-500">{item.description}</p>
                    ) : null}
                    <p className="mt-1 text-xs text-gray-600">
                      Qté {item.quantity}
                      <span className="text-gray-400"> · </span>
                      {item.frequencyLabel}
                    </p>
                    {item.hasDiscount ? (
                      <dl className="mt-2 space-y-0.5 text-xs text-gray-700">
                        <div>
                          <dt className="sr-only">Prix initial</dt>
                          <dd>
                            Prix initial : {formatCentsAsEuros(item.unitInitialCents)} € HT
                            {item.periodSuffix}
                          </dd>
                        </div>
                        <div>
                          <dt className="sr-only">Remise</dt>
                          <dd className="text-indigo-800">
                            Remise commerciale : {item.discountValueLabel}
                          </dd>
                        </div>
                        <div>
                          <dt className="sr-only">Portée</dt>
                          <dd>Appliquée : {item.discountScopeLabel}</dd>
                        </div>
                        <div>
                          <dt className="sr-only">Prix remisé</dt>
                          <dd className="font-semibold text-emerald-800">
                            Prix remisé : {formatCentsAsEuros(item.unitAfterCents)} € HT
                            {item.periodSuffix}
                          </dd>
                        </div>
                      </dl>
                    ) : (
                      <p className="mt-2 text-sm font-medium text-gray-900">
                        {formatCentsAsEuros(item.unitInitialCents)} € HT{item.periodSuffix}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              {block ? (
                <dl
                  className="space-y-1 border-t border-gray-100 pt-2"
                  data-testid="nr2j-quote-billing-totals"
                >
                  {hasDiscount ? (
                    <>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">
                          Sous-total avant remises{block.periodSuffix}
                        </dt>
                        <dd className="font-medium">
                          {formatCentsAsEuros(block.totalGrossHtCents)} €{block.periodSuffix}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">
                          Remises commerciales{block.periodSuffix}
                        </dt>
                        <dd className="font-medium text-indigo-800">
                          −{formatCentsAsEuros(block.totalDiscountHtCents)} €
                          {block.periodSuffix}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">
                          Total HT après remises{block.periodSuffix}
                        </dt>
                        <dd className="font-medium">
                          {formatCentsAsEuros(block.totalHtCents)} €{block.periodSuffix}
                        </dd>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Total HT{block.periodSuffix}</dt>
                      <dd className="font-medium">
                        {formatCentsAsEuros(block.totalHtCents)} €{block.periodSuffix}
                      </dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-gray-500">TVA{block.periodSuffix}</dt>
                    <dd className="font-medium">
                      {formatCentsAsEuros(block.totalVatCents)} €{block.periodSuffix}
                    </dd>
                  </div>
                  <div className="flex justify-between border-t border-gray-100 pt-1">
                    <dt className="font-semibold text-gray-900">
                      Total TTC{block.periodSuffix}
                    </dt>
                    <dd className="font-bold text-gray-900">
                      {formatCentsAsEuros(block.totalTtcCents)} €{block.periodSuffix}
                    </dd>
                  </div>
                </dl>
              ) : null}
            </div>
          );
        })}
        {dueAtSubscription.show ? (
          <div className="border-t border-gray-100 pt-3" data-testid="nr2j-quote-due-subscription">
            <dl className="space-y-1">
              {dueAtSubscription.parts.map((part) => (
                <div key={part.billingType} className="flex justify-between">
                  <dt className="text-gray-500">{part.label}</dt>
                  <dd className="font-medium">{formatCentsAsEuros(part.totalTtcCents)} €</dd>
                </div>
              ))}
              <div className="flex justify-between border-t border-gray-100 pt-1">
                <dt className="font-semibold text-gray-900">Total à la souscription</dt>
                <dd className="font-bold text-gray-900">
                  {formatCentsAsEuros(dueAtSubscription.totalTtcCents)} €
                </dd>
              </div>
              {dueAtSubscription.recurringAfter.map((after) => (
                <div key={`after-${after.billingType}`} className="flex justify-between pt-1">
                  <dt className="text-gray-500">{after.label}</dt>
                  <dd className="font-medium text-gray-700">
                    {formatCentsAsEuros(after.totalTtcCents)} €{after.periodSuffix}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </div>
    </div>
  );
}
